import type { InventoryRecordItemResponseDto } from '../../../api';
import type { ProductData, ProductItem } from './types';

/** Record line shape from /detailed (and legacy snake_case fields). */
export type RecordItemLike = Pick<
  InventoryRecordItemResponseDto,
  'id' | 'productId' | 'quantity' | 'product' | 'individualUnits'
> & {
  product_id?: number;
};

export function sortRecordItemsByLineId(items: RecordItemLike[]): RecordItemLike[] {
  return [...items].sort((a, b) => {
    const idA = a.id != null ? Number(a.id) : NaN;
    const idB = b.id != null ? Number(b.id) : NaN;
    if (Number.isNaN(idA) && Number.isNaN(idB)) {
      return 0;
    }
    if (Number.isNaN(idA)) {
      return 1;
    }
    if (Number.isNaN(idB)) {
      return -1;
    }
    return idA - idB;
  });
}

export function resolveRecordItemProductId(item: RecordItemLike): number | null {
  const raw = item.productId ?? item.product_id ?? item.product?.id;
  if (raw == null) {
    return null;
  }
  const productId = Number(raw);
  return Number.isInteger(productId) && productId > 0 ? productId : null;
}

/**
 * Prefer API `item.product` for display; fall back to local catalog cache.
 * Edit hydration must not depend on the warm 20-product page.
 */
export function resolveRecordItemProductDisplay(
  item: RecordItemLike,
  cache: ProductData[],
): { productId: number; productName: string; productCode: string; workshopCode?: string } | null {
  const productId = resolveRecordItemProductId(item);
  if (productId == null) {
    return null;
  }

  const cached = cache.find((product) => product.id === productId);
  const productName = (cached?.name ?? item.product?.name ?? '').trim();
  const productCode = (cached?.code ?? item.product?.code ?? '').trim();
  const nestedWorkshopCode = (item.product as { workshopCode?: string } | undefined)?.workshopCode;
  const workshopCode = (cached?.workshopCode ?? nestedWorkshopCode ?? '').trim() || undefined;

  if (productName === '' || productCode === '') {
    return null;
  }

  return { productId, productName, productCode, workshopCode };
}

export function mapRecordItemsToProductItems(
  items: RecordItemLike[],
  cache: ProductData[],
): ProductItem[] {
  const sortedItems = sortRecordItemsByLineId(items);
  const productItems: ProductItem[] = [];

  sortedItems.forEach((item, index) => {
    const display = resolveRecordItemProductDisplay(item, cache);
    if (display == null) {
      return;
    }

    productItems.push({
      id: index + 1,
      productId: display.productId,
      productName: display.productName,
      productCode: display.productCode,
      workshopCode: display.workshopCode,
      quantity: item.quantity != null ? String(item.quantity) : '0',
      originalItemId: item.id,
    });
  });

  return productItems;
}

export function extractProductIdsFromRecordItems(items: RecordItemLike[]): number[] {
  const ids = new Set<number>();
  for (const item of items) {
    const productId = resolveRecordItemProductId(item);
    if (productId != null) {
      ids.add(productId);
    }
  }
  return Array.from(ids);
}

export function collectHydratedRecordItemIds(items: ProductItem[]): Set<number> {
  const hydratedIds = new Set<number>();
  for (const item of items) {
    const lineId = item.originalItemId;
    if (lineId != null && Number(lineId) > 0) {
      hydratedIds.add(Number(lineId));
    }
  }
  return hydratedIds;
}
